import apiClient from "@/api/apiClient";
import { extractApiData } from "@/utils/apiResponseHandler";
import type { Tag } from "@/types";

class TagAPI {
  /**
   * Get all available tags
   * @returns Promise resolving to an array of tag objects
   */
  async fetchAll(): Promise<Tag[]> {
    return await apiClient.get("/tag").then(extractApiData);
  }

  /**
   * Create a new tag
   * @param name The name of the tag to create
   * @returns Promise resolving to the created tag object
   */
  async create(name: string): Promise<Tag> {
    return await apiClient.post("/tag", { name }).then(extractApiData);
  }

  /**
   * Update a tag
   * @param tagId The ID of the tag to update
   * @param name The new name for the tag
   * @returns Promise resolving to the updated tag object
   */
  async updateTag(tagId: number, name: string): Promise<Tag> {
    return await apiClient.put(`/tag/${tagId}`, { name }).then(extractApiData);
  }

  /**
   * Delete a tag
   * @param tagId The ID of the tag to delete
   * @returns Promise resolving to a success message
   */
  async deleteTag(tagId: number): Promise<void> {
    return await apiClient.delete(`/tag/${tagId}`).then(extractApiData);
  }

  /**
   * Get all tags for a specific risk
   * @param riskId The ID of the risk
   * @returns Promise resolving to an array of tag objects
   */
  async fetchRiskTags(riskId: number): Promise<Tag[]> {
    return await apiClient.get(`/risk/${riskId}/tags`).then(extractApiData);
  }

  /**
   * Add tags to a risk
   * @param riskId The ID of the risk
   * @param tags Array of tag names to add
   * @param replace Whether to replace existing tags (true) or add to them (false)
   * @returns Promise resolving to the updated array of tag objects
   */
  async addTagsToRisk(
    riskId: number,
    tags: string[],
    replace = false
  ): Promise<Tag[]> {
    return await apiClient
      .post(`/risk/${riskId}/tags${replace ? "?replace=true" : ""}`, { tags })
      .then(extractApiData);
  }

  /**
   * Remove a tag from a risk
   * @param riskId The ID of the risk
   * @param tagId The ID of the tag to remove
   * @returns Promise resolving to a success message
   */
  async removeTagFromRisk(riskId: number, tagId: number): Promise<void> {
    return await apiClient
      .delete(`/risk/${riskId}/tags/${tagId}`)
      .then(extractApiData);
  }

  /**
   * Get all tags for a specific measure
   * @param measureId The ID of the measure
   * @returns Promise resolving to an array of tag objects
   */
  async fetchMeasureTags(measureId: number): Promise<Tag[]> {
    return await apiClient.get(`/measure/${measureId}/tags`).then(extractApiData);
  }

  /**
   * Add tags to a measure
   * @param measureId The ID of the measure
   * @param tags Array of tag names to add
   * @param replace Whether to replace existing tags (true) or add to them (false)
   * @returns Promise resolving to the updated array of tag objects
   */
  async addTagsToMeasure(
    measureId: number,
    tags: string[],
    replace = false
  ): Promise<Tag[]> {
    return await apiClient
      .post(`/measure/${measureId}/tags${replace ? "?replace=true" : ""}`, {
        tags,
      })
      .then(extractApiData);
  }

  /**
   * Remove a tag from a measure
   * @param measureId The ID of the measure
   * @param tagId The ID of the tag to remove
   * @returns Promise resolving to a success message
   */
  async removeTagFromMeasure(measureId: number, tagId: number): Promise<void> {
    return await apiClient
      .delete(`/measure/${measureId}/tags/${tagId}`)
      .then(extractApiData);
  }
}

export default new TagAPI();

